import React, { useEffect, useMemo, useState } from "react";
import api from "../api/api";
import toast from "react-hot-toast";
import { Wallet, Search, Edit2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

type Row = {
  id: string;
  name: string;
  email: string;
  department: string | null;
  role: string;
  leaveBalanceDays: number;
  annualLeaveDays: number;
  casualLeaveDays: number;
};

export default function LeaveBalances() {
  const { user } = useAuth();
  const { permissions } = useAuth() as any;
  const isAdmin = user?.role === "ADMIN";
  const canEditBalance = isAdmin || !!permissions?.find((p: any) => p.module === "leave-balances" && p.canEdit);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<Row | null>(null);
  const [annualVal, setAnnualVal] = useState("");
  const [casualVal, setCasualVal] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get<Row[]>("/requests/balances")
      .then(r => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      r.name.toLowerCase().includes(t) ||
      r.email.toLowerCase().includes(t) ||
      (r.department || "").toLowerCase().includes(t)
    );
  }, [rows, q]);

  const openEdit = (r: Row) => {
    setEditing(r);
    setAnnualVal(String(r.annualLeaveDays ?? 15));
    setCasualVal(String(r.casualLeaveDays ?? 6));
  };

  const save = async () => {
    if (!editing) return;
    const annual = Number(annualVal);
    const casual = Number(casualVal);
    if (isNaN(annual) || annual < 0 || annual > 365) return toast.error("Annual: enter 0-365");
    if (isNaN(casual) || casual < 0 || casual > 365) return toast.error("Casual: enter 0-365");
    setSaving(true);
    try {
      await api.post(`/requests/balance/${editing.id}`, { annual, casual });
      toast.success("Balance updated");
      setEditing(null);
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const colorFor = (val: number, low: number) =>
    val > low ? "text-green-600" : val > 0 ? "text-amber-600" : "text-red-600";

  return (
    <div className="max-w-5xl mx-auto p-3 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Wallet className="text-emerald-600" size={26}/>
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Leave Balances</h1>
            <p className="text-xs sm:text-sm text-gray-500">Annual & Casual leave per employee</p>
          </div>
        </div>
        <div className="relative w-full sm:w-auto">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search..."
            className="w-full sm:w-64 pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"/>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-gray-500">Loading...</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Employee</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Department</th>
                <th className="text-right px-4 py-3 font-medium text-blue-700">🏖️ Annual</th>
                <th className="text-right px-4 py-3 font-medium text-purple-700">🏝️ Casual</th>
                {canEditBalance && <th className="px-4 py-3"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map(r => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{r.name}</div>
                    <div className="text-xs text-gray-500">{r.role}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{r.department || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-bold ${colorFor(r.annualLeaveDays ?? 0, 3)}`}>
                      {(r.annualLeaveDays ?? 0).toFixed(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`font-bold ${colorFor(r.casualLeaveDays ?? 0, 1)}`}>
                      {(r.casualLeaveDays ?? 0).toFixed(1)}
                    </span>
                  </td>
                  {canEditBalance && (
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => openEdit(r)}
                        className="text-blue-600 hover:text-blue-800" title="Edit balances">
                        <Edit2 size={16}/>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={canEditBalance ? 5 : 4} className="px-4 py-8 text-center text-gray-400">No employees found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b bg-emerald-50">
              <div className="text-lg font-semibold text-emerald-800">Edit Balance</div>
              <div className="text-xs text-emerald-600">{editing.name}</div>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-sm font-medium text-blue-700 mb-1">🏖️ Annual (days)</label>
                <input type="number" min="0" max="365" step="0.5" value={annualVal} onChange={e => setAnnualVal(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
              </div>
              <div>
                <label className="block text-sm font-medium text-purple-700 mb-1">🏝️ Casual (days)</label>
                <input type="number" min="0" max="365" step="0.5" value={casualVal} onChange={e => setCasualVal(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"/>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-100 text-sm">Cancel</button>
                <button onClick={save} disabled={saving}
                  className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm disabled:opacity-50">
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
